/**
 * Iter 231 · Wave 4 · Tranche 2 — Partner Wave4 page.
 * One page keyed off partner.kind: hospital · builder · insurance.
 * Auth via partnerApi (service-partner JWT).
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Buildings, Bank, ShieldCheck, Bed, Coins, MapPin,
  Warning, Plus, Files,
} from "@phosphor-icons/react";
import {
  partnerApi, fmtPartnerErr, clearPartnerToken, isPartnerAuthed,
} from "../../lib/partnerApi";

export default function PartnerWave4() {
  const nav = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isPartnerAuthed()) {
      nav("/service-partner/login", { replace: true });
      return;
    }
    partnerApi.get("/service-partners/me")
      .then(r => setPartner(r.data.partner))
      .catch(e => {
        if (e?.response?.status === 401) {
          clearPartnerToken();
          nav("/service-partner/login", { replace: true });
        } else toast.error(fmtPartnerErr(e));
      })
      .finally(() => setLoading(false));
  }, []);   // eslint-disable-line

  if (loading) return <div className="p-8">Loading…</div>;
  if (!partner) return null;

  const kind = partner.kind;
  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="partner-wave4-root">
      <div className="mb-4">
        <h1 className="font-heading text-2xl">
          AirQ · {kind ? kind[0].toUpperCase()+kind.slice(1) : "Partner"} Portal
        </h1>
        <p className="text-sm text-[#5C5A55] mt-1">
          Logged in as <b>{partner.name}</b> · kind = {kind || "unspecified"}
        </p>
      </div>
      {kind === "hospital"   && <HospitalPanel/>}
      {kind === "builder"    && <BuilderPanel/>}
      {kind === "insurance"  && <InsurancePanel/>}
      {!["hospital","builder","insurance"].includes(kind) && (
        <div className="bg-white rounded-lg border p-6 text-sm">
          <Warning size={20} className="text-[#B45309] inline mr-2"/>
          Your account is <code>kind={kind || "unset"}</code>. Wave 4 partner
          surfaces need <code>hospital</code>, <code>builder</code>, or{" "}
          <code>insurance</code>.
        </div>
      )}
    </div>
  );
}


// =====================================================
// Hospital
// =====================================================
function HospitalPanel() {
  const [tab, setTab] = useState("admissions");
  return (
    <div className="space-y-3" data-testid="hospital-panel">
      <TabBar tabs={[
        ["admissions", "Admissions"],
        ["beds", "Bed board"],
        ["pads", "Helipad status"],
      ]} value={tab} onChange={setTab}/>
      {tab === "admissions" && <HospitalAdmissions/>}
      {tab === "beds"       && <HospitalBeds/>}
      {tab === "pads"       && <HospitalPads/>}
    </div>
  );
}
function HospitalAdmissions() {
  const [rows, setRows] = useState([]);
  const load = () => partnerApi.get("/partner/wave4/hospital/admissions")
    .then(r => setRows(r.data.admissions || []))
    .catch(e => toast.error(fmtPartnerErr(e)));
  useEffect(load, []);
  const act = async (mid, action) => {
    try {
      await partnerApi.post(`/partner/wave4/hospital/admissions/${mid}/action`, { action });
      toast.success(`Admission ${action}`); load();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
  };
  return (
    <div className="bg-white rounded-lg border p-4" data-testid="hosp-admissions">
      <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">Incoming admissions · {rows.length}</div>
      {rows.length === 0 ? <div className="text-sm text-[#8A8780]">No incoming AirQ admissions</div>
        : rows.map(m => (
          <div key={m.id} className="flex items-center justify-between py-2 border-b last:border-0 text-sm"
               data-testid={`hosp-adm-${m.id.slice(0,6)}`}>
            <div>
              <div className="font-medium">{m.mission_no} · {m.patient_name || "unnamed"}</div>
              <div className="text-[11px] text-[#8A8780]">
                {m.mission_kind} · P{m.priority?.replace("P","")} · ETA {m.aircraft_call_sign || "tbd"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded bg-[#F5F3EE] text-[10px] uppercase">
                {m.admission_status || "pending"}
              </span>
              {!m.admission_status && (
                <>
                  <button onClick={() => act(m.id, "accept")}
                            data-testid={`hosp-accept-${m.id.slice(0,6)}`}
                            className="text-[11px] px-2 py-1 rounded bg-[#166534] text-white">Accept</button>
                  <button onClick={() => act(m.id, "reject")}
                            data-testid={`hosp-reject-${m.id.slice(0,6)}`}
                            className="text-[11px] px-2 py-1 rounded bg-[#7F1D1D] text-white">Reject</button>
                </>
              )}
            </div>
          </div>
        ))}
    </div>
  );
}
function HospitalBeds() {
  const [b, setB] = useState({ icu_total: 0, icu_free: 0, er_total: 0, er_free: 0,
                                     ot_total: 0, ot_free: 0,
                                     ventilators_total: 0, ventilators_free: 0 });
  const load = () => partnerApi.get("/partner/wave4/hospital/bed-board")
    .then(r => setB(prev => ({ ...prev, ...r.data })))
    .catch(e => toast.error(fmtPartnerErr(e)));
  useEffect(load, []);
  const save = async () => {
    try {
      const p = Object.fromEntries(
        Object.entries(b).filter(([k]) => k.match(/^(icu|er|ot|ventilators)_/))
                          .map(([k, v]) => [k, parseInt(v || 0)]));
      await partnerApi.post("/partner/wave4/hospital/bed-board", p);
      toast.success("Bed board saved");
    } catch (e) { toast.error(fmtPartnerErr(e)); }
  };
  const cell = (label, key_total, key_free, color) => (
    <div className="p-4 bg-white rounded-lg border">
      <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">{label}</div>
      <div className="flex items-center gap-3">
        <div>
          <div className="text-[10px] text-[#8A8780]">Total</div>
          <input type="number" value={b[key_total]||0}
                   data-testid={`bed-${key_total}`}
                   onChange={e => setB({...b, [key_total]: e.target.value})}
                   className="w-20 border rounded px-2 py-1 text-sm"/>
        </div>
        <div>
          <div className="text-[10px] text-[#8A8780]">Free</div>
          <input type="number" value={b[key_free]||0}
                   data-testid={`bed-${key_free}`}
                   onChange={e => setB({...b, [key_free]: e.target.value})}
                   className="w-20 border rounded px-2 py-1 text-sm"
                   style={{color}}/>
        </div>
      </div>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="hosp-beds">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cell("ICU",         "icu_total", "icu_free", "#166534")}
        {cell("ER",          "er_total",  "er_free",  "#B45309")}
        {cell("OT",          "ot_total",  "ot_free",  "#4338CA")}
        {cell("Ventilators", "ventilators_total", "ventilators_free", "#7C2D12")}
      </div>
      <button onClick={save} data-testid="bed-save"
                className="px-4 py-2 rounded bg-[#0F766E] text-white text-sm">Save bed board</button>
    </div>
  );
}
function HospitalPads() {
  const [data, setData] = useState(null);
  useEffect(() => {
    partnerApi.get("/partner/wave4/hospital/helipad-status")
      .then(r => setData(r.data)).catch(e => toast.error(fmtPartnerErr(e)));
  }, []);
  if (!data) return <div className="text-sm text-[#8A8780]">Loading…</div>;
  return (
    <div className="bg-white rounded-lg border p-4" data-testid="hosp-pads">
      <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">
        Nearby pads · {data.count} · {data.active_missions.length} live
      </div>
      {data.pads.map(p => (
        <div key={p.id} className="flex items-center justify-between py-2 border-b last:border-0 text-sm">
          <div className="flex items-center gap-2">
            <MapPin size={14} className={p.occupied ? "text-[#B91C1C]" : "text-[#166534]"}/>
            <span className="font-medium">{p.name}</span>
            <span className="text-[11px] text-[#8A8780]">{p.kind}</span>
          </div>
          <span className={`text-[10px] uppercase font-medium ${p.occupied ? "text-[#B91C1C]" : "text-[#166534]"}`}>
            {p.occupied ? "Occupied" : "Free"}
          </span>
        </div>
      ))}
    </div>
  );
}


// =====================================================
// Builder
// =====================================================
function BuilderPanel() {
  const [tab, setTab] = useState("pads");
  return (
    <div className="space-y-3" data-testid="builder-panel">
      <TabBar tabs={[
        ["pads", "Pads"],
        ["mou", "MoU"],
        ["revenue", "Revenue"],
        ["maintenance", "Maintenance"],
      ]} value={tab} onChange={setTab}/>
      {tab === "pads"        && <BuilderPads/>}
      {tab === "mou"         && <BuilderMou/>}
      {tab === "revenue"     && <BuilderRevenue/>}
      {tab === "maintenance" && <BuilderMaint/>}
    </div>
  );
}
function BuilderPads() {
  const [data, setData] = useState({ pads: [], approvals: [] });
  const [showAdd, setShowAdd] = useState(false);
  const load = () => partnerApi.get("/partner/wave4/builder/pads")
    .then(r => setData(r.data)).catch(e => toast.error(fmtPartnerErr(e)));
  useEffect(load, []);
  return (
    <div className="space-y-3" data-testid="bld-pads">
      <div className="flex justify-between">
        <div className="text-sm text-[#5C5A55]">
          {data.pads_count || 0} approved pads · {data.approvals_count || 0} in flight
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="bld-pad-add"
                  className="flex items-center gap-1 text-xs px-3 py-1.5 bg-[#0369A1] text-white rounded">
          <Plus size={12}/>Submit pad
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">Approved pads</div>
          {(data.pads || []).map(p => (
            <div key={p.id} className="py-1.5 border-b last:border-0 text-sm">
              <div className="font-medium">{p.name}</div>
              <div className="text-[11px] text-[#8A8780]">{p.kind} · {p.district || "?"}</div>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">Approvals</div>
          {(data.approvals || []).map(a => (
            <div key={a.id} className="py-1.5 border-b last:border-0 text-sm">
              <div className="font-medium">{a.approval_no} · {a.proposed_pad?.name}</div>
              <div className="text-[11px] text-[#8A8780]">{a.status}</div>
            </div>
          ))}
        </div>
      </div>
      {showAdd && <BuilderPadSubmit onClose={() => setShowAdd(false)}
                                             onSaved={() => { setShowAdd(false); load(); }}/>}
    </div>
  );
}
function BuilderPadSubmit({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", kind: "hospital_helipad", lat: "", lng: "",
                                     district: "", csr_public_use: false });
  const submit = async () => {
    try {
      await partnerApi.post("/partner/wave4/builder/pads/submit", {
        ...f, lat: parseFloat(f.lat), lng: parseFloat(f.lng) });
      toast.success("Submitted for approval"); onSaved();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
  };
  return (
    <Modal onClose={onClose} title="Submit pad for approval">
      <Input placeholder="Pad name" value={f.name} onChange={v => setF({...f, name:v})} testid="bld-name"/>
      <select value={f.kind} onChange={e => setF({...f, kind:e.target.value})}
                data-testid="bld-kind"
                className="w-full border rounded px-3 py-2 mb-2 text-sm bg-white">
        <option value="helipad">Helipad</option>
        <option value="hospital_helipad">Hospital helipad</option>
        <option value="hybrid">Hybrid</option>
      </select>
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Latitude" value={f.lat} onChange={v => setF({...f, lat:v})} testid="bld-lat"/>
        <Input placeholder="Longitude" value={f.lng} onChange={v => setF({...f, lng:v})} testid="bld-lng"/>
      </div>
      <Input placeholder="District" value={f.district} onChange={v => setF({...f, district:v})} testid="bld-dist"/>
      <label className="flex items-center gap-2 text-sm mb-3">
        <input type="checkbox" checked={f.csr_public_use}
                 onChange={e => setF({...f, csr_public_use: e.target.checked})}/>
        Mark pad as CSR public-use
      </label>
      <button onClick={submit} data-testid="bld-save"
                className="w-full py-2 rounded bg-[#0369A1] text-white">Submit</button>
    </Modal>
  );
}
function BuilderMou() {
  const [rows, setRows] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const load = () => partnerApi.get("/partner/wave4/builder/mou")
    .then(r => setRows(r.data.mou || [])).catch(e => toast.error(fmtPartnerErr(e)));
  useEffect(load, []);
  return (
    <div className="space-y-3" data-testid="bld-mou">
      <div className="flex justify-between">
        <div className="text-sm text-[#5C5A55]">{rows.length} MoUs</div>
        <button onClick={() => setShowAdd(true)} data-testid="bld-mou-add"
                  className="flex items-center gap-1 text-xs px-3 py-1.5 bg-[#7C3AED] text-white rounded">
          <Plus size={12}/>MoU
        </button>
      </div>
      <div className="bg-white rounded-lg border p-4">
        {rows.length === 0 ? <div className="text-sm text-[#8A8780]">No MoUs yet</div>
          : rows.map(m => (
            <div key={m.id} className="py-2 border-b last:border-0 text-sm">
              <div className="font-medium">{m.title}</div>
              <div className="text-[11px] text-[#8A8780]">
                <a href={m.doc_url} target="_blank" rel="noreferrer" className="text-[#0369A1] underline">
                  Doc
                </a> · valid till {m.valid_till || "—"}
                {m.exclusivity && " · exclusive"}
              </div>
            </div>
          ))}
      </div>
      {showAdd && <BuilderMouSubmit onClose={() => setShowAdd(false)}
                                             onSaved={() => { setShowAdd(false); load(); }}/>}
    </div>
  );
}
function BuilderMouSubmit({ onClose, onSaved }) {
  const [f, setF] = useState({ title: "", doc_url: "", valid_till: "",
                                     exclusivity: false });
  const submit = async () => {
    try {
      await partnerApi.post("/partner/wave4/builder/mou", f);
      toast.success("MoU uploaded"); onSaved();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
  };
  return (
    <Modal onClose={onClose} title="Upload MoU">
      <Input placeholder="Title" value={f.title} onChange={v => setF({...f, title:v})} testid="mou-title"/>
      <Input placeholder="Doc URL" value={f.doc_url} onChange={v => setF({...f, doc_url:v})} testid="mou-url"/>
      <Input placeholder="Valid till (YYYY-MM-DD)" value={f.valid_till} onChange={v => setF({...f, valid_till:v})} testid="mou-till"/>
      <label className="flex items-center gap-2 text-sm mb-3">
        <input type="checkbox" checked={f.exclusivity}
                 onChange={e => setF({...f, exclusivity: e.target.checked})}/>
        Exclusivity
      </label>
      <button onClick={submit} data-testid="mou-save"
                className="w-full py-2 rounded bg-[#7C3AED] text-white">Upload</button>
    </Modal>
  );
}
function BuilderRevenue() {
  const [d, setD] = useState(null);
  useEffect(() => {
    partnerApi.get("/partner/wave4/builder/revenue?days=90")
      .then(r => setD(r.data)).catch(e => toast.error(fmtPartnerErr(e)));
  }, []);
  if (!d) return <div className="text-sm text-[#8A8780]">Loading…</div>;
  return (
    <div className="grid grid-cols-3 gap-3" data-testid="bld-rev">
      <Stat label="Landings (90d)" v={d.landings} c="#0F766E"/>
      <Stat label="Revenue" v={`₹${Number(d.revenue).toLocaleString("en-IN")}`} c="#B45309"/>
      <Stat label="Rate / landing" v={`₹${d.rate_per_landing}`} c="#4338CA"/>
    </div>
  );
}
function BuilderMaint() {
  const [data, setData] = useState({ pads: [] });
  const [pad, setPad] = useState("");
  const [ok, setOk] = useState(true);
  const [note, setNote] = useState("");
  useEffect(() => {
    partnerApi.get("/partner/wave4/builder/pads")
      .then(r => setData(r.data)).catch(e => toast.error(fmtPartnerErr(e)));
  }, []);
  const submit = async () => {
    if (!pad) return toast.error("Pick a pad");
    try {
      await partnerApi.post("/partner/wave4/builder/maintenance", {
        pad_id: pad, checklist: { night_lighting: ok, wind_sock: ok, surface: ok },
        note, all_clear: ok });
      toast.success("Maintenance log saved");
    } catch (e) { toast.error(fmtPartnerErr(e)); }
  };
  return (
    <div className="bg-white rounded-lg border p-4 space-y-2" data-testid="bld-maint">
      <div className="text-xs uppercase tracking-wider text-[#8A8780]">Log maintenance check</div>
      <select value={pad} onChange={e => setPad(e.target.value)}
                data-testid="maint-pad"
                className="w-full border rounded px-3 py-2 text-sm bg-white">
        <option value="">Select pad</option>
        {(data.pads || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={ok} onChange={e => setOk(e.target.checked)}
                 data-testid="maint-ok"/>
        All-clear (night lighting · wind sock · surface)
      </label>
      <Input placeholder="Note" value={note} onChange={setNote} testid="maint-note"/>
      <button onClick={submit} data-testid="maint-save"
                className="px-4 py-2 rounded bg-[#0F766E] text-white text-sm">Log check</button>
    </div>
  );
}


// =====================================================
// Insurance
// =====================================================
function InsurancePanel() {
  const [tab, setTab] = useState("policies");
  return (
    <div className="space-y-3" data-testid="insurance-panel">
      <TabBar tabs={[
        ["policies", "Policies"],
        ["claims", "Claim queue"],
        ["fraud", "Fraud flags"],
      ]} value={tab} onChange={setTab}/>
      {tab === "policies" && <InsPolicies/>}
      {tab === "claims"   && <InsClaims/>}
      {tab === "fraud"    && <InsFraud/>}
    </div>
  );
}
function InsPolicies() {
  const [rows, setRows] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const load = () => partnerApi.get("/partner/wave4/insurance/policies")
    .then(r => setRows(r.data.policies || [])).catch(e => toast.error(fmtPartnerErr(e)));
  useEffect(load, []);
  return (
    <div className="space-y-3" data-testid="ins-pol">
      <div className="flex justify-between">
        <div className="text-sm text-[#5C5A55]">{rows.length} active policies</div>
        <button onClick={() => setShowAdd(true)} data-testid="ins-pol-add"
                  className="flex items-center gap-1 text-xs px-3 py-1.5 bg-[#166534] text-white rounded">
          <Plus size={12}/>Policy
        </button>
      </div>
      <div className="bg-white rounded-lg border p-4">
        {rows.length === 0 ? <div className="text-sm text-[#8A8780]">No policies</div>
          : rows.map(p => (
            <div key={p.id} className="py-2 border-b last:border-0 text-sm">
              <div className="font-medium">{p.policy_no} · {p.holder_name}</div>
              <div className="text-[11px] text-[#8A8780]">
                ceiling ₹{Number(p.coverage_ceiling).toLocaleString("en-IN")}
                {p.valid_till && ` · valid till ${p.valid_till}`}
              </div>
            </div>
          ))}
      </div>
      {showAdd && <InsPolicyModal onClose={() => setShowAdd(false)}
                                            onSaved={() => { setShowAdd(false); load(); }}/>}
    </div>
  );
}
function InsPolicyModal({ onClose, onSaved }) {
  const [f, setF] = useState({ policy_no: "", holder_name: "", holder_phone: "",
                                     coverage_ceiling: 0, valid_till: "" });
  const submit = async () => {
    try {
      await partnerApi.post("/partner/wave4/insurance/policies",
        { ...f, coverage_ceiling: parseFloat(f.coverage_ceiling) });
      toast.success("Policy added"); onSaved();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
  };
  return (
    <Modal onClose={onClose} title="New policy">
      <Input placeholder="Policy no" value={f.policy_no} onChange={v => setF({...f, policy_no:v})} testid="pol-no"/>
      <Input placeholder="Holder name" value={f.holder_name} onChange={v => setF({...f, holder_name:v})} testid="pol-name"/>
      <Input placeholder="Holder phone" value={f.holder_phone} onChange={v => setF({...f, holder_phone:v})} testid="pol-phone"/>
      <Input placeholder="Coverage ceiling (₹)" value={f.coverage_ceiling} onChange={v => setF({...f, coverage_ceiling:v})} testid="pol-ceil"/>
      <Input placeholder="Valid till (YYYY-MM-DD)" value={f.valid_till} onChange={v => setF({...f, valid_till:v})} testid="pol-till"/>
      <button onClick={submit} data-testid="pol-save"
                className="w-full py-2 rounded bg-[#166534] text-white">Add policy</button>
    </Modal>
  );
}
function InsClaims() {
  const [d, setD] = useState({ claims: [] });
  useEffect(() => {
    partnerApi.get("/partner/wave4/insurance/claims?days=90")
      .then(r => setD(r.data)).catch(e => toast.error(fmtPartnerErr(e)));
  }, []);
  return (
    <div className="bg-white rounded-lg border p-4" data-testid="ins-claims">
      <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">
        Claim queue · {d.count || 0} · schemes: {(d.matched_schemes || []).join(", ") || "none"}
      </div>
      {(d.claims || []).map(c => (
        <div key={c.id} className="flex items-center justify-between py-2 border-b last:border-0 text-sm">
          <div>
            <div className="font-medium">{c.claim_no}</div>
            <div className="text-[11px] text-[#8A8780]">
              {c.scheme_code} · ₹{Number(c.amount_claimed).toLocaleString("en-IN")}
            </div>
          </div>
          <span className="px-2 py-0.5 rounded bg-[#F5F3EE] text-[10px] uppercase">{c.status}</span>
        </div>
      ))}
    </div>
  );
}
function InsFraud() {
  const [d, setD] = useState({ flags: [] });
  useEffect(() => {
    partnerApi.get("/partner/wave4/insurance/fraud-flags")
      .then(r => setD(r.data)).catch(e => toast.error(fmtPartnerErr(e)));
  }, []);
  return (
    <div className="bg-white rounded-lg border p-4" data-testid="ins-fraud">
      <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">
        Fraud flags · {d.count || 0}
      </div>
      {(d.flags || []).length === 0 ? <div className="text-sm text-[#8A8780]">No flags — clean run</div>
        : (d.flags || []).map((f, i) => (
          <div key={i} className="py-1.5 border-b last:border-0 text-sm">
            <Warning size={12} className="inline text-[#B91C1C] mr-1"/>
            <span className="font-medium">{f.reason}</span>
            <span className="text-[11px] text-[#8A8780] ml-2">
              {f.claim_id ? `claim ${f.claim_id.slice(0,8)}` : ""}
              {f.customer_id ? `cust ${f.customer_id.slice(0,8)} on ${f.date} × ${f.count}` : ""}
              {f.amount ? ` · ₹${f.amount} vs ${f.distance_km}km` : ""}
            </span>
          </div>
        ))}
    </div>
  );
}


// =====================================================
// Shared UI
// =====================================================
function TabBar({ tabs, value, onChange }) {
  return (
    <div className="flex items-center gap-1 border-b border-[#E5E2D9] overflow-x-auto">
      {tabs.map(([k, l]) => (
        <button key={k} onClick={() => onChange(k)}
                  data-testid={`partner-w4-tab-${k}`}
                  className={`px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap ${
                    value===k ? "border-[#0F766E] text-[#0F766E]"
                                    : "border-transparent text-[#5C5A55]"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}
function Input({ placeholder, value, onChange, testid }) {
  return (
    <input placeholder={placeholder} value={value || ""}
             onChange={e => onChange(e.target.value)}
             data-testid={testid}
             className="w-full border rounded px-3 py-2 mb-2 text-sm"/>
  );
}
function Stat({ label, v, c }) {
  return (
    <div className="p-4 bg-white rounded-lg border">
      <div className="text-[22px] font-heading font-semibold" style={{color:c}}>{v}</div>
      <div className="text-[11px] uppercase tracking-[0.15em] text-[#8A8780] mt-1">{label}</div>
    </div>
  );
}
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg">{title}</h2>
          <button onClick={onClose} className="text-2xl leading-none">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
